import { useMemo } from 'react'
import { useTicketActivity, type TicketActivity } from './useTicketActivity'
import { useEventsByIds } from './useEventsByIds'
import { useUserProfiles } from './useUserProfiles'
import { isEventActive } from '../lib/formatDate'
import { getNow } from '../lib/clock'
import { offersTicketsAndPregames } from '@shared/pregames'
import type { Event } from '../types'

export interface CrewTicketAction extends TicketActivity {
  event: Event
}

/**
 * Ticket things waiting on the viewer in Crew's "Waiting on you", on parties
 * that haven't ended, newest first. Each stays until it's resolved.
 */
export function useCrewTicketActions(uid: string | null) {
  const tickets = useTicketActivity(uid)
  const eventIds = useMemo(() => [...new Set(tickets.items.map(t => t.eventId))], [tickets.items])
  const userIds = useMemo(() => [...new Set(tickets.items.map(t => t.userId))], [tickets.items])
  const { events, pending } = useEventsByIds(eventIds)
  const profiles = useUserProfiles(userIds)

  const items = useMemo(() => {
    const now = getNow()
    const activeById = new Map(events.filter(e => offersTicketsAndPregames(e) && isEventActive(e, now)).map(e => [e.id, e]))
    return tickets.items
      .flatMap((t): CrewTicketAction[] => {
        const event = activeById.get(t.eventId)
        return event ? [{ ...t, event }] : []
      })
      .sort((a, b) => b.at - a.at)
  }, [tickets.items, events])

  return { items, profiles, loading: tickets.loading || (eventIds.length > 0 && pending) }
}
